import { z } from 'zod';
import { timeframeSchema } from './timeframe.js';
/**
 * How many recent bars each timeframe is scanned over for gaps.
 *
 * Per-timeframe rather than one flat number, because a flat number means very
 * different things per frame: 1000 M1 bars is under a day, so the M1 row would
 * almost always read zero, while 1000 W1 bars is more history than the broker
 * holds. These are each roughly a useful lookback.
 *
 * Shared between the chart page and the summary endpoint on purpose. When they
 * used different windows the sidebar reported a different count from the chart
 * beside it, which reads as a bug even though both numbers were correct.
 */
/**
 * Timeframes the gaps module works on.
 *
 * M1 alone. A gap is a range no trade passed through, which is a fact about the
 * FEED rather than about a timeframe — and M1 is the finest series stored, so
 * it is the only one that sees them as they actually happened. Every coarser
 * series inherits the same gaps with their edges rounded to its own bar grid,
 * which is not a second opinion, only a blurrier copy of the first.
 *
 * The aggregated frames were worse than redundant. Their scan windows reach
 * back years, so an unfilled daily gap from 2013 counted as much as one from an
 * hour ago, and since gold has risen a long way they sat almost entirely below
 * price — which pinned the read bearish regardless of anything happening today.
 */
export const GAP_TIMEFRAMES = ['M1'];
/** @deprecated Use GAP_TIMEFRAMES — kept so an old import still resolves. */
export const GAP_BIAS_TIMEFRAMES = GAP_TIMEFRAMES;
/**
 * Bars each timeframe is scanned over.
 *
 * M1 covers roughly the last month. The count is measured rather than derived
 * — gold does not trade at weekends, so clock arithmetic overshoots badly:
 * 60x24x7 = 10,080 "one week" bars actually reach back 9.3 calendar days. The
 * figures below were taken from the data: 7,000 bars lands on 7.1 days and
 * 30,000 on 30.
 *
 * A month rather than a week because an unfilled gap does not expire. Widening
 * the scan 4x took the unfilled count from 23 to 30 while the FILLED count went
 * from 643 to 2,461 — which is why the chart draws them in one canvas pass
 * instead of a series each (see gapPrimitive).
 *
 * The other entries are vestigial: nothing scans them since the module became
 * M1-only. They stay because the type is keyed by Timeframe.
 */
export const GAP_SCAN_BARS = {
    M1: 30_000,
    M5: 3000,
    M15: 2000,
    M30: 2000,
    H1: 2000,
    H4: 1500,
    H4_1: 1500,
    H4_2: 1500,
    H4_3: 1500,
    D1: 1000,
    W1: 500,
};
export const gapModeSchema = z.enum(['untraded', 'range']);
export const gapSchema = z.object({
    id: z.string(),
    time: z.number().int(),
    prevTime: z.number().int(),
    direction: z.enum(['up', 'down']),
    level: z.number(),
    edge: z.number(),
    size: z.number(),
    sizePips: z.number(),
    filled: z.boolean(),
    filledTime: z.number().int().nullable(),
});
export const gapsResponseSchema = z.object({
    symbol: z.string(),
    timeframe: timeframeSchema,
    digits: z.number().int(),
    storedGmtOffsetSec: z.number().int(),
    /** Gaps still open, newest last. */
    gaps: z.array(gapSchema),
    /** How many were found before the unfilled/size filters were applied. */
    totalDetected: z.number().int(),
});
//# sourceMappingURL=gap.js.map